/**
 * assets/loft-configurador-accesorio.js
 *
 * Comportamiento del configurador visual reutilizable (snippets/loft-configurador-accesorio.liquid).
 * No depende del tipo de accesorio (nochero, espejo, cojín, etc.) — todo lo específico del
 * producto ya viene resuelto en el bloque JSON que genera el snippet.
 *
 * Decisiones de diseño importantes (para quien edite esto después):
 *
 * 1) El cambio de imagen principal NO se hace manipulando el <img> del carrusel a mano.
 *    Se usa el método público que el propio Dawn ya expone en su componente <media-gallery>
 *    (`setActiveMedia`), el mismo que el tema usa internamente al cambiar de variante con
 *    imagen asociada. Así el zoom, el modal y el slider de miniaturas siguen funcionando
 *    exactamente igual — no se reemplaza nada del carrusel, solo se le pide que muestre otra
 *    foto que ya está entre sus medios.
 *
 * 2) LOFT 2026-09-12 — CAMBIO DE CRITERIO. Antes la sección permanecía oculta (`hidden`) hasta
 *    el primer cambio real de variante disparado por el cliente, porque Shopify siempre deja
 *    una variante preseleccionada y se interpretaba que "después de elegir la medida" exigía
 *    una interacción real. En la práctica eso escondía la venta cruzada detrás de un clic que
 *    el cliente no tenía motivo para hacer: al abrir la ficha no aparecía "Elige tu nochero".
 *    Ahora el bloque se revela en la carga (ver `sembrarEstadoInicial`, más abajo), sembrando
 *    `state.currentBedVariantId` con la variante que Shopify ya dejó preseleccionada.
 *
 *    Esa siembra es imprescindible, no cosmética: revelar el bloque a secas (por CSS, por
 *    ejemplo) dejaría `currentBedVariantId` en null y entonces applyTotalFor() buscaría
 *    priceByVariant[null] — el cliente elegiría un nochero, el accesorio sí se agregaría al
 *    carrito, y el total mostrado en la ficha seguiría siendo el de la cama sola. Verificado
 *    en vivo antes de este cambio. Si por lo que sea no se logra resolver la variante inicial,
 *    el bloque NO se revela y se conserva el comportamiento anterior: preferimos no mostrarlo
 *    a mostrarlo con un precio incorrecto.
 *
 * 2b) Por qué `reveal()` acepta `inmediato`: la clase `is-visible` (la que lleva la opacidad de
 *    0 a 1) se añadía siempre dentro de un requestAnimationFrame para que la transición de
 *    entrada se viera. Eso funcionaba cuando `reveal()` solo corría tras un clic — si el
 *    cliente hace clic, la pestaña está en primer plano y rAF corre. Al revelar en la carga eso
 *    deja de ser cierto: si la ficha se abre en una pestaña de fondo, el navegador congela rAF,
 *    la clase nunca se añade y el bloque queda ocupando su espacio con opacity 0 — un hueco
 *    invisible. Verificado en preview con document.hidden === true. Por eso la revelación
 *    inicial pasa `inmediato = true` (sin fade: el bloque ya estaba ahí desde el primer render)
 *    y la revelación por interacción conserva el rAF y su transición.
 *
 * 3) Dawn publica PUB_SUB_EVENTS.variantChange DESPUÉS de que su propio código ya intentó poner
 *    la imagen destacada de la nueva variante (ver product-info.js, updateMedia()). Por eso este
 *    listener, que corre después, siempre puede "reafirmar" la imagen del accesorio seleccionado
 *    sin pelear una condición de carrera.
 *
 * 4) El accesorio NUNCA se agrega tocando el <form> nativo del producto. Se deja que Dawn agregue
 *    la cama exactamente como siempre. Solo después de que Dawn confirma ese agregado
 *    (PUB_SUB_EVENTS.cartUpdate), este script hace una segunda llamada propia a /cart/add.js para
 *    el accesorio —con la cantidad elegida (1 o 2)— y vuelve a pedirle al cart drawer que se
 *    repinte con la respuesta de esa segunda llamada.
 *
 *    Importante: Dawn publica cartUpdate SIN envolver el payload en una clave `data` (a
 *    diferencia de variantChange, que sí lo hace). Ver product-form.js:
 *    publish(PUB_SUB_EVENTS.cartUpdate, { source, productVariantId, cartData }). Verificado en
 *    vivo: el payload real está en el propio `evt`, no en `evt.data`.
 *
 * 5) Cuando el configurador está presente, se oculta el botón de pago dinámico (Shop Pay /
 *    "Comprar ahora") del formulario del producto, porque ese botón salta directo al checkout
 *    de Shopify sin pasar por este script (limitación de Shopify, no de este código). Esta
 *    ocultación es local a esta página (solo corre si existe `.loft-config` en el DOM).
 *
 * 6) Cantidad (1 o 2): cada tarjeta trae su propio `data-max-qty` (calculado en Liquid a partir
 *    del inventario real del accesorio), así que el botón "2" se deshabilita solo cuando el
 *    inventario controlado no alcanza — sin inventar disponibilidad que el carrito no pueda
 *    cumplir. Si aun así el carrito rechaza la cantidad (p. ej. alguien más compró la última
 *    unidad segundos antes), se muestra un mensaje de error visible en vez de fallar en silencio.
 */
(function () {
  function initConfigurator(root) {
    var metaEl = root.querySelector('[data-loft-config-meta]');
    var meta;
    try {
      meta = JSON.parse(metaEl.textContent);
    } catch (e) {
      return;
    }

    var sectionId = meta.sectionId;
    var totalEl = root.querySelector('[data-loft-config-total]');
    var errorEl = root.querySelector('[data-loft-config-error]');
    var propertyInput = root.querySelector('[data-loft-config-property]');
    var propertyName = root.dataset.propertyName || 'Accesorio';
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-loft-card]'));
    var qtyWrap = root.querySelector('[data-loft-config-qty]');
    var qtyButtons = Array.prototype.slice.call(root.querySelectorAll('[data-loft-qty-btn]'));
    var availabilityWrap = root.querySelector('[data-loft-config-availability]');
    var availabilityBadge = root.querySelector('[data-loft-config-availability-badge]');
    var optionsByVariantId = {};
    (meta.options || []).forEach(function (opt) {
      optionsByVariantId[opt.variantId] = opt;
    });

    var state = {
      revealed: false,
      currentBedVariantId: null,
      selectedAccessoryVariantId: '', // '' = "Sin accesorio"
      selectedQuantity: 1,
    };

    function getGallery() {
      return document.querySelector('#MediaGallery-' + sectionId);
    }

    function applyMediaFor(accessoryVariantId, bedVariantId) {
      var gallery = getGallery();
      if (!gallery || typeof gallery.setActiveMedia !== 'function') return;

      var mediaId;
      if (accessoryVariantId && optionsByVariantId[accessoryVariantId]) {
        mediaId = optionsByVariantId[accessoryVariantId].mediaId;
      } else {
        mediaId = meta.baseMediaIdByVariant[bedVariantId];
      }
      if (!mediaId) return;
      gallery.setActiveMedia(mediaId, false);
    }

    function applyTotalFor(accessoryVariantId, bedVariantId, quantity) {
      if (!totalEl) return;
      var priceText;
      if (accessoryVariantId && optionsByVariantId[accessoryVariantId]) {
        var byVariant = optionsByVariantId[accessoryVariantId].priceByVariant[bedVariantId];
        priceText = byVariant && byVariant[String(quantity)];
      } else {
        priceText = meta.basePriceByVariant[bedVariantId];
      }
      if (priceText) totalEl.textContent = priceText;
    }

    function clearError() {
      if (!errorEl) return;
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = false;
    }

    function setQuantityUI(card) {
      if (!qtyWrap) return;
      var hasAccessory = !!card.dataset.variantId;
      qtyWrap.hidden = !hasAccessory;
      if (!hasAccessory) return;

      var maxQty = parseInt(card.dataset.maxQty || '2', 10);
      qtyButtons.forEach(function (btn) {
        var qty = parseInt(btn.dataset.qty, 10);
        var disabled = qty > maxQty;
        btn.disabled = disabled;
        btn.classList.toggle('loft-config__qty-btn--disabled', disabled);
      });

      // Si la cantidad elegida antes ya no cabe en el máximo de esta tarjeta, vuelve a 1.
      if (state.selectedQuantity > maxQty) {
        state.selectedQuantity = 1;
      }
      qtyButtons.forEach(function (btn) {
        var qty = parseInt(btn.dataset.qty, 10);
        btn.classList.toggle('is-selected', qty === state.selectedQuantity);
      });
    }

    function setAvailabilityUI(card) {
      if (!availabilityWrap || !availabilityBadge) return;
      var hasAccessory = !!card.dataset.variantId;
      availabilityWrap.hidden = !hasAccessory;
      if (!hasAccessory) return;

      availabilityBadge.textContent = card.dataset.availabilityLabel || '';
      availabilityBadge.className = 'loft-config__badge ' + (card.dataset.availabilityClass || '');
    }

    function updatePropertyInput(card) {
      if (!propertyInput) return;
      var variantId = card.dataset.variantId || '';
      if (!variantId) {
        propertyInput.value = '';
        propertyInput.disabled = true;
        return;
      }
      var label = card.dataset.label || '';
      if (state.selectedQuantity > 1) label += ' x' + state.selectedQuantity;
      propertyInput.value = label;
      propertyInput.disabled = false;
    }

    function refreshForCurrentSelection() {
      applyMediaFor(state.selectedAccessoryVariantId, state.currentBedVariantId);
      applyTotalFor(state.selectedAccessoryVariantId, state.currentBedVariantId, state.selectedQuantity);
    }

    function selectCard(card) {
      cards.forEach(function (c) {
        c.classList.remove('is-selected');
        c.setAttribute('aria-selected', 'false');
      });
      card.classList.add('is-selected');
      card.setAttribute('aria-selected', 'true');
      clearError();

      var variantId = card.dataset.variantId || '';
      state.selectedAccessoryVariantId = variantId;
      state.selectedQuantity = variantId ? 1 : 0;

      setQuantityUI(card);
      setAvailabilityUI(card);
      updatePropertyInput(card);
      refreshForCurrentSelection();
    }

    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        if (card.hasAttribute('disabled')) return;
        selectCard(card);
      });
    });

    qtyButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        state.selectedQuantity = parseInt(btn.dataset.qty, 10);
        qtyButtons.forEach(function (b) {
          b.classList.toggle('is-selected', b === btn);
        });
        var selectedCard = cards.filter(function (c) {
          return c.classList.contains('is-selected');
        })[0];
        if (selectedCard) updatePropertyInput(selectedCard);
        refreshForCurrentSelection();
      });
    });

    // `inmediato = true` salta el requestAnimationFrame (ver nota 2b de la cabecera).
    function reveal(inmediato) {
      if (state.revealed) return;
      state.revealed = true;
      root.hidden = false;
      if (inmediato) {
        root.classList.add('is-visible');
        return;
      }
      window.requestAnimationFrame(function () {
        root.classList.add('is-visible');
      });
    }

    function onVariantChange(variant) {
      if (!variant) return;
      state.currentBedVariantId = String(variant.id);
      reveal();
      refreshForCurrentSelection();
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, function (evt) {
        onVariantChange(evt && evt.data && evt.data.variant);
      });
    }

    // --- Botón de pago dinámico: oculto mientras este configurador exista en la página. ---
    var productForm = root.closest('product-info') || document;
    productForm.querySelectorAll('.shopify-payment-button').forEach(function (el) {
      el.style.display = 'none';
    });

    // --- Agregar el accesorio (con su cantidad) justo después de que Dawn agregó la cama. ---
    var bedVariantIds = Object.keys(meta.basePriceByVariant || {});
    var pendingAdd = false;

    // --- LOFT 2026-09-12: estado inicial visible (ver notas 2 y 2b de la cabecera). ---
    // Dawn NO publica variantChange en la carga de la página, así que sin esto el bloque
    // se quedaba oculto hasta que el cliente cambiaba de medida. Tomamos la variante que
    // Shopify ya dejó preseleccionada del JSON que imprime el propio <variant-selects>.
    //
    // A propósito NO se llama aquí a applyMediaFor(): Dawn ya resolvió la imagen inicial del
    // carrusel y forzar setActiveMedia en el arranque haría saltar la foto principal. El total
    // tampoco hace falta recalcularlo: Liquid ya lo imprimió con el precio de esta variante.
    (function sembrarEstadoInicial() {
      var seed =
        document.querySelector('#variant-selects-' + sectionId + ' [data-selected-variant]') ||
        document.querySelector('[data-selected-variant]');
      if (seed) {
        try {
          var v = JSON.parse(seed.textContent);
          if (v && v.id) state.currentBedVariantId = String(v.id);
        } catch (e) {}
      }
      if (!state.currentBedVariantId && bedVariantIds.length === 1) {
        state.currentBedVariantId = bedVariantIds[0];
      }
      // Sin variante resuelta no se revela: mejor no mostrarlo que mostrarlo con precio erróneo.
      if (state.currentBedVariantId) reveal(true);
    })();

    function addAccessoryToCart(accessoryVariantId, quantity) {
      if (pendingAdd) return;
      pendingAdd = true;

      var cartComponent = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      var body = {
        items: [
          {
            id: accessoryVariantId,
            quantity: quantity,
            properties: { _loft_config_de: propertyName },
          },
        ],
      };
      if (cartComponent && typeof cartComponent.getSectionsToRender === 'function') {
        body.sections = cartComponent.getSectionsToRender().map(function (s) {
          return s.id;
        });
        body.sections_url = window.location.pathname;
      }

      fetch(window.routes ? window.routes.cart_add_url : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (response) {
          if (response && response.status) {
            showError(
              'Agregamos la cama, pero no pudimos agregar el accesorio elegido: ' +
                (response.description || response.message || 'sin stock suficiente') +
                '. Revisa el carrito.'
            );
            return;
          }
          if (cartComponent && typeof cartComponent.renderContents === 'function') {
            cartComponent.renderContents(response);
          }
        })
        .catch(function (err) {
          showError('Agregamos la cama, pero hubo un problema de conexión al agregar el accesorio. Revisa el carrito.');
          console.error('loft-configurador-accesorio: no se pudo agregar el accesorio al carrito', err);
        })
        .finally(function () {
          pendingAdd = false;
        });
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (evt) {
        var data = evt;
        if (!data || data.source !== 'product-form') return;
        if (bedVariantIds.indexOf(String(data.productVariantId)) === -1) return;
        if (!state.selectedAccessoryVariantId || state.selectedQuantity < 1) return;
        addAccessoryToCart(state.selectedAccessoryVariantId, state.selectedQuantity);
      });
    }
  }

  function init() {
    document.querySelectorAll('[data-loft-config]').forEach(initConfigurator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
